import { type Mode, MODES } from "#src/constants/mode.ts";
import { type ProductType, PRODUCT_TYPE } from "#src/constants/products.ts";
import { CONCEPT_TYPE } from "#src/constants/concepts.ts";
import {
  type LivingBenefitDataItem,
  type ModeDataItem,
  type ParamSuggestion,
  type ParsingData,
  type QuoteRequestParams,
  type RenderRules,
  insuredQuoteData,
  parseBoolean,
  parseType,
} from "#src/types/QuoteParams.ts";
import { type ProductBase } from "#src/types/products/ProductBase.ts";

const TERM_TYPE = {
  [PRODUCT_TYPE.TERM]: PRODUCT_TYPE.TERM,
  [PRODUCT_TYPE.NON_MED]: PRODUCT_TYPE.NON_MED,
};

export type TermType = (typeof TERM_TYPE)[keyof typeof TERM_TYPE];

export interface TermProductType extends ProductBase {
  permitFetchableType: (type: TermType) => void;
  isTerm: boolean;
  fetchableTypes: string[];
  termLengths: number[];
  requestCategoryGenerator: (termLength: number | null) => string[];
  livingBenefits: LivingBenefitDataItem[];
  giveParamsSuggestion?: ParamSuggestion;
  defaults: {
    active_years: number;
    living_benefits: boolean;
    save_age: boolean;
  };
  eAppParser(args: Partial<ParsingData>): TermProductType["defaults"] & {
    face_amount: number;
    mode: Mode;
  };
}

const requestCategories = {
  [PRODUCT_TYPE.TERM]: "term.none",
  [PRODUCT_TYPE.NON_MED]: "term.none.non_med",
};

function showHealth(conversion: boolean) {
  return !conversion;
}

function BaseTermQuoteParams(
  type: TermType | typeof CONCEPT_TYPE.TERM
): TermProductType {
  let requestCategory = "";
  const categories: string[] = [];
  const fetchableTypes: string[] = [];

  if (type && requestCategories[type]) {
    requestCategory = requestCategories[type];
    categories.push(requestCategory);
    fetchableTypes.push(type);
  }

  return {
    get requestCategory(): string {
      return requestCategory;
    },
    get fetchableTypes(): string[] {
      return fetchableTypes;
    },
    get type(): ProductType {
      return type;
    },
    get isTerm(): boolean {
      return true;
    },
    permitFetchableType(type): void {
      if (!requestCategories[type]) throw "Must be a supported type";
      fetchableTypes.push(type);
      categories.push(requestCategories[type]);
    },
    renderRules(
      _quoteParams,
      options = { conversion: false }
    ): Partial<RenderRules> {
      return {
        saveAge: true,
        termLength: true,
        faceAmount: true,
        livingBenefits: true,
        insuredHealth: showHealth(options.conversion),
        mode: true,
        state: true,
      };
    },
    get termLengths(): number[] {
      return [40, 35, 30, 25, 20, 15, 10];
    },
    get livingBenefits(): LivingBenefitDataItem[] {
      return [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ];
    },
    get modes(): ModeDataItem[] {
      return MODES;
    },
    get display(): string {
      return "Term";
    },
    get defaults() {
      return {
        active_years: 20,
        living_benefits: false,
        save_age: false,
      };
    },
    requestCategoryGenerator(termLength = null): string[] {
      const reqCatGen = (cat: string, len: number | null) =>
        len ? `${cat}.${len}_year` : cat;

      return categories.map(v => reqCatGen(v, termLength));
    },
    eAppParser(model): ReturnType<TermProductType["eAppParser"]> {
      return {
        active_years: parseType<number>(
          model.active_years,
          this.termLengths,
          this.defaults.active_years
        ),
        mode: model.mode as Mode,
        living_benefits: Boolean(model.living_benefits),
        save_age: parseBoolean(model.save_age, this.defaults.save_age),
        face_amount: model?.face_amount || 1000000,
      };
    },
    giveParamsSuggestion(age): string {
      if (age < 18) {
        return "The minimum age for Term is 18";
      }
      return "";
    },
    toQuoteRequest(args): Partial<QuoteRequestParams> {
      return {
        agent: { id: args.agentId },
        compare: false,

        ...insuredQuoteData(args.insured),

        selected_type: args.params.selected_type,
        state: args.params.state,
        categories: this.requestCategoryGenerator(
          args.params.active_years || null
        ),

        mode: args.params.mode,
        face_amount: args.params.face_amount,
        living_benefits: args.params.living_benefits || undefined,
        save_age: args.params.save_age,
      };
    },
  };
}

export function Term() {
  return BaseTermQuoteParams(PRODUCT_TYPE.TERM);
}

export function NonMedQuoteParams() {
  return BaseTermQuoteParams(PRODUCT_TYPE.NON_MED);
}

//NOTE(tim): there has to be a better way than this. introduce composites
export function TermConceptParams() {
  return BaseTermQuoteParams(CONCEPT_TYPE.TERM);
}
